import { h, Fragment } from "preact";
import { useMemo, useState } from "preact/hooks";
import sortMaterials from "../../helpers/sortMaterials/sortMaterials";
import ProductOptionSwatchesGroup from "./product-option-swatches-group";
import ProductOptionSummary from "./product-option-summary";
import ProductOptionPreview from "./product-option-preview";
import ProductOptionRequestSwatch from "./product-option-request-swatch";

/**
 * @returns {{title: ?string, optionValues: Array}[]} A null title means the
 *   values carry no material group, so the group renders without a sub-label.
 */
const buildMaterialGroups = (optionValues) => {
  const groups = {};

  optionValues.forEach((optionValue) => {
    const title = optionValue.materialGroup || "";

    if (!(title in groups)) groups[title] = [];

    groups[title].push(optionValue);
  });

  return Object.entries(groups)
    .sort(([titleA], [titleB]) => sortMaterials(titleA, titleB))
    .map(([title, values]) => ({
      title: title || null,
      optionValues: values,
    }));
};

/**
 * Material groups, optionally preceded by a split into a Standard section then
 * a Made For You section holding their own groups.
 *
 * @returns {{madeToOrder: ?boolean, materialGroups: Array}[]} Sections in
 *   display order; only non-empty sections are returned. A null madeToOrder is
 *   the unsplit case — one section holding every value.
 */
const buildSections = (optionValues, splitMadeToOrder) => {
  const sections = splitMadeToOrder
    ? [false, true].map((madeToOrder) => ({
        madeToOrder,
        materialGroups: buildMaterialGroups(
          optionValues.filter(
            (optionValue) => !!optionValue.madeToOrder === madeToOrder,
          ),
        ),
      }))
    : [
        {
          madeToOrder: null,
          materialGroups: buildMaterialGroups(optionValues),
        },
      ];

  return sections.filter(({ materialGroups }) => materialGroups.length);
};

export default function ProductOptionSwatchesGrouped({
  optionValues,
  optionName,
  optionNumber,
  updateOptionValue,
  selectedOptionValue,
  disabledOptionValues = [],
  variantsByOptionValue = [],
  unavailableOptionValues = [],
  waitlistOptionValues = [],
  selectedVariant,
  openByDefault,
  enableSwatchPreview,
  disabledSwatchStyle,
  optionSize,
  splitMadeToOrder,
}) {
  const [previewOpen, setPreviewOpen] = useState(false);

  const sections = useMemo(
    () => buildSections(optionValues, splitMadeToOrder),
    [optionValues, splitMadeToOrder],
  );

  // A lone section has nothing to contrast with, so it renders unlabelled
  const showSectionHeadings = sections.length > 1;

  return (
    <conditional-details>
      <details
        className="product-options__option-details product-options__swatches-grouped"
        open={openByDefault}
      >
        <ProductOptionSummary
          optionName={optionName}
          optionNumber={optionNumber}
          selectedOptionValue={selectedOptionValue}
        />
        <div
          className="product-options__option-content"
          data-content
          style={{ overflow: openByDefault ? "visible" : null }}
        >
          <div className="product-options__option-content-inner">
            {enableSwatchPreview && (
              <ProductOptionPreview
                selectedVariant={selectedVariant}
                selectedOptionValue={selectedOptionValue}
                open={previewOpen}
                setOpen={setPreviewOpen}
                optionInGroupSelected={true}
              />
            )}
            <div className="product-options__swatch-sections">
              {sections.map(({ madeToOrder, materialGroups }) => (
                <div
                  key={String(madeToOrder)}
                  className="product-options__swatch-section"
                >
                  {showSectionHeadings && (
                    <div className="product-options__swatch-section-title">
                      {madeToOrder
                        ? window.localeStrings.product.option_group_made_for_you
                        : window.localeStrings.product.option_group_standard}
                    </div>
                  )}
                  <div className="product-options__swatch-groups">
                    {materialGroups.map(({ title, optionValues }) => (
                      <ProductOptionSwatchesGroup
                        key={title || "ungrouped"}
                        groupTitle={title}
                        optionValues={optionValues}
                        selectedOptionValue={selectedOptionValue}
                        selectedVariant={selectedVariant}
                        variantsByOptionValue={variantsByOptionValue}
                        disabledOptionValues={disabledOptionValues}
                        unavailableOptionValues={unavailableOptionValues}
                        waitlistOptionValues={waitlistOptionValues}
                        updateOptionValue={updateOptionValue}
                        optionName={optionName}
                        enableSwatchPreview={enableSwatchPreview}
                        disabledSwatchStyle={disabledSwatchStyle}
                        optionSize={optionSize}
                        setPreviewOpen={setPreviewOpen}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {enableSwatchPreview && (
              <ProductOptionRequestSwatch selectedVariant={selectedVariant} />
            )}
          </div>
        </div>
      </details>
    </conditional-details>
  );
}
